/**
 * RFICard Component
 * Displays a single RFI with status badge, number, subject, and key metadata.
 * Follows PermitCard pattern (Gold Standard).
 */
import { RFI, RFI_STATUS_CONFIG } from '@/types/rfis';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RFICardProps {
  rfi: RFI;
  onClick: (rfi: RFI) => void;
  onDelete: (rfiId: string) => void;
}

function getRFICardColorClass(rfi: RFI): string {
  switch (rfi.status) {
    case 'draft':
      return 'border-gray-300 bg-gray-50';
    case 'open':
      return 'border-blue-400 bg-blue-50';
    case 'answered':
      return 'border-green-500 bg-green-50';
    case 'closed':
      return 'border-slate-400 bg-slate-50';
    default:
      return 'border-gray-300 bg-gray-50';
  }
}

export function RFICard({ rfi, onClick, onDelete }: RFICardProps) {
  const { label, className } = RFI_STATUS_CONFIG[rfi.status];
  const fileCount = (rfi.file_count_question ?? 0) + (rfi.file_count_answer ?? 0);

  return (
    <Card
      className={cn(
        'p-4 hover:shadow-md transition-all cursor-pointer',
        getRFICardColorClass(rfi)
      )}
      data-testid="rfi-card"
      onClick={() => onClick(rfi)}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm font-mono text-muted-foreground">{rfi.rfi_number}</span>
            <h4 className="font-medium">{rfi.subject}</h4>
            <Badge className={className}>{label}</Badge>
          </div>

          <div className="text-sm text-muted-foreground space-y-1">
            {rfi.assigned_to && (
              <p>Assigned to: {rfi.assigned_to}</p>
            )}
            {rfi.date_due && (
              <p>Due: {new Date(rfi.date_due).toLocaleDateString()}</p>
            )}
            {rfi.date_issued && (
              <p>Issued: {new Date(rfi.date_issued).toLocaleDateString()}</p>
            )}
            {rfi.date_answered && (
              <p>Answered: {new Date(rfi.date_answered).toLocaleDateString()}</p>
            )}
            {fileCount > 0 && (
              <p className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                {fileCount} {fileCount === 1 ? 'file' : 'files'}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            data-testid="delete-rfi"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(rfi.id);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
